import { AdvisorInfoWithWritingCode, PARTNER_PREFIX, returnSafeString } from './carrier-utils';

export const NATIONWIDE_UNDERWRITING_PREFILL_SECTIONS = [
  'client-info',
  'smoking',
  'medications-medical',
  'mental-health',
  'mobility',
  'recent-medical-care',
  'daily-activities',
];

export const NATIONWIDE_PREFILL_SECTIONS_PRIMARY = [
  'proposed-insured',
  'insurance-information',
  'temporary-insurance',
  'tobacco-nicotine',
  'health-conditions',
];

export const NATIONWIDE_PREFILL_SECTIONS_SECONDARY = [
  'proposed-additional-insured',
  'insurance-information',
  'insurance-information-additional-insured',
  'additional-insured-insurance',
  'temporary-insurance',
  'tobacco-nicotine',
  'health-conditions',
];
export const mapLtcBenefitPeriodToOptionId = (
  ltcBenefitDurationMonths: number | undefined,
): string => {
  if (!ltcBenefitDurationMonths) return '';
  const years = ltcBenefitDurationMonths / 12;
  switch (years) {
    case 2:
      return '2-years';
    case 3:
      return '3-years';
    case 4:
      return '4-years';
    case 5:
      return '5-years';
    case 6:
      return '6-years';
    case 7:
      return '7-years';
    default:
      return '';
  }
};

export const mapInflationProtectionToOptionId = (
  inflationBenefitOption: string | undefined,
): string => {
  if (!inflationBenefitOption) {
    return 'reject-all';
  }
  const optionLower = inflationBenefitOption.toLowerCase();
  if (optionLower.includes('5%') && optionLower.includes('compound')) {
    return '5-compound';
  }
  if (optionLower.includes('3%') && optionLower.includes('compound')) {
    if (optionLower.includes('20 year')) {
      return '3-compound-20';
    }
    return '3-compound';
  }
  if (optionLower.includes('reject') || optionLower.includes('none')) {
    return 'reject-all';
  }
  return 'reject-all';
};

export const mapLtcMonthlyPaymentsToOptionId = (
  ltcBenefitDurationMonths: number | undefined,
): string => {
  if (!ltcBenefitDurationMonths) return '';

  switch (ltcBenefitDurationMonths) {
    case 48:
      return '48-payments';
    case 72:
      return '72-payments';
    case 96:
      return '96-payments';
    default:
      return '';
  }
};

export const mapProductFundingOptionToOptionId = (
  productFundingOption: string | undefined,
): string => {
  if (!productFundingOption) return '';

  const optionLower = productFundingOption.toLowerCase();
  if (optionLower.includes('single premium') && !optionLower.includes('recurring')) {
    return 'option1';
  }
  if (
    optionLower.includes('recurring') &&
    !optionLower.includes('lump sum') &&
    !optionLower.includes('single')
  ) {
    return 'option2';
  }
  if (optionLower.includes('lump sum') && optionLower.includes('recurring')) {
    return 'option3';
  }
  return '';
};

export const getAdvisorUpdatesForNationwide = (
  advisorInfo: AdvisorInfoWithWritingCode,
): PreFillUpdate[] => {
  return [
    {
      questionRef: 'financialProfessionalNameCert',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'firm',
      answer: advisorInfo.organizationDisplayName || advisorInfo.organizationName || '',
    },
    {
      questionRef: 'financialProfessionalPhone',
      answer: advisorInfo.advisorBusinessPhoneNumber || advisorInfo.advisorPhoneNumber || '',
    },
    {
      questionRef: 'financialProfessionalEmailCert',
      answer: advisorInfo.advisorEmail,
    },
    {
      questionRef: 'agentNameCert',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'agentEmailCert',
      answer: advisorInfo.advisorEmail,
    },
    {
      questionRef: 'agentPhone',
      answer: advisorInfo.advisorBusinessPhoneNumber || advisorInfo.advisorPhoneNumber || '',
    },
    {
      questionRef: 'nationwideNumber',
      answer: advisorInfo.bgaWritingCode || '',
    },
  ].filter(update => update.answer);
};

export const getClientUpdatesForNationwide = (clientInfo: Client): PreFillUpdate[] => {
  const clientUpdates = [
    {
      questionRef: 'proposedInsuredName',
      answer: clientInfo.clientFullName,
    },
    {
      questionRef: 'proposedInsuredEmail',
      answer: clientInfo.clientEmail,
      sourceQuestionRefs: ['clientEmail'],
    },
  ].filter(update => update.answer);

  const clientPartnerUpdates = clientInfo.mutableClientPartner
    ? [
        {
          questionRef: 'additionalInsuredName',
          answer: clientInfo.mutableClientPartner.clientFullName,
        },
        {
          questionRef: 'additionalInsuredEmail',
          answer: clientInfo.mutableClientPartner.clientEmail,
          sourceQuestionRefs: [PARTNER_PREFIX + 'clientEmail'],
        },
      ].filter(update => update.answer)
    : [];

  const jointOwnerUpdates = clientInfo.mutableClientPartner
    ? [
        {
          questionRef: 'jointOwnerFirstName',
          answer: clientInfo.mutableClientPartner.clientFirstName,
        },
        {
          questionRef: 'jointOwnerLastName',
          answer: clientInfo.mutableClientPartner.clientLastName,
        },
        {
          questionRef: 'jointOwnerEmail',
          answer: clientInfo.mutableClientPartner.clientEmail,
        },
      ].filter(update => update.answer)
    : [];

  return [...clientUpdates, ...clientPartnerUpdates, ...jointOwnerUpdates];
};

export const getPolicyUpdatesForNationwide = (
  policyFundingSource: PolicyFundingSource,
): PreFillUpdate[] => {
  let initialQuote = policyFundingSource.sourceQuote;
  if (typeof initialQuote === 'string') {
    try {
      initialQuote = JSON.parse(initialQuote);
    } catch (e) {
      console.error('Failed to parse sourceQuote:', e);
      initialQuote = null;
    }
  }

  // Type guard to check if sourceQuote is a PolicyQuote (not Traditional LTC)
  const isPolicyQuote = (quote: any): quote is PolicyQuote => {
    return quote && typeof quote === 'object' && 'ltcBenefitDurationMonths' in quote;
  };

  const sourceQuote = isPolicyQuote(initialQuote) ? initialQuote : null;

  return [
    {
      questionRef: 'caremattersIIBaseAmount',
      answer: returnSafeString(policyFundingSource.policyMaximumBenefitAmount),
    },
    {
      questionRef: 'ltcSpecifiedBenefitPeriod',
      answer: mapLtcBenefitPeriodToOptionId(sourceQuote?.ltcBenefitDurationMonths),
    },
    {
      questionRef: 'ltcInflationProtection',
      answer: mapInflationProtectionToOptionId(sourceQuote?.inflationBenefitOption),
    },
    {
      questionRef: 'premiumOption',
      answer: mapProductFundingOptionToOptionId(sourceQuote?.productFundingOption),
    },
    {
      questionRef: 'refundOfPremiumOption',
      answer: 'minimum-refund-maximum-ltc',
    },
    {
      questionRef: 'singlePremiumPayment',
      answer: returnSafeString(sourceQuote?.premiumAmount ?? null),
    },
    {
      questionRef: 'singlePremiumAmount',
      answer: returnSafeString(sourceQuote?.premiumAmount ?? null),
    },
    {
      questionRef: 'caremattersTogetherBaseAmount',
      answer: returnSafeString(policyFundingSource.policyMaximumBenefitAmount),
    },
    {
      questionRef: 'maxMonthlyLtcPayments',
      answer: mapLtcMonthlyPaymentsToOptionId(sourceQuote?.ltcBenefitDurationMonths),
    },
    {
      questionRef: 'togetherInflationProtection',
      answer: mapInflationProtectionToOptionId(sourceQuote?.inflationBenefitOption),
    },
  ].filter(update => update.answer);
};

export const getAdvisorUpdatesForNationwideUnderwriting = (
  advisorInfo: Advisor,
): PreFillUpdate[] => {
  return [
    {
      questionRef: 'financialProfessionalName',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'telephoneNumber',
      answer: advisorInfo.advisorBusinessPhoneNumber || advisorInfo.advisorPhoneNumber || '',
    },
    {
      questionRef: 'email',
      answer: advisorInfo.advisorEmail,
    },
  ].filter(update => update.answer);
};

export const getJointOwnerUpdatesForNationwide = (
  preApplicationData: PrefillData[],
): PreFillUpdate[] => {

  const findPreAppValue = (questionRef: string): string => {
    const item = preApplicationData.find(data => data.questionRef === questionRef);
    const answer = item?.answer;
    return typeof answer === 'string' ? answer : '';
  };

  const firstName = findPreAppValue('jointOwnerFirstName');
  const lastName = findPreAppValue('jointOwnerLastName');
  const email = findPreAppValue('jointOwnerEmail');

  // Build full name if we have first and last name
  const fullName = firstName && lastName ? `${firstName} ${lastName}` : '';

  // Note: No sourceQuestionRefs because these are derived/combined from pre-app data
  return [
    {
      questionRef: 'jointPolicyOwnerName',
      answer: fullName,
    },
    {
      questionRef: 'jointPolicyOwnerEmail',
      answer: email,
    },
  ].filter(update => update.answer);
};
